import { useEffect, useMemo, useState } from 'react'

import { useReportQuestionCards } from '@/features/studies/report/page/components/views/overview/useReportQuestionCards'
import { quickStudiesApi } from '@/lib/api/quick-study-native'
import { getAllQuestions } from '@/lib/utils/chart-utils'

import { QuickStudyExploreData } from '@digital-twins/shared-types'
import { AssetReportContextItem } from '@digital-twins/deck-ui/components/charts/asset-report-label'
import { SegmentationMapping } from '@/types/components/audiences/segmentation-mapping'

/**
 * What a Quick Study report question reads from its run's frozen survey: the
 * SurveyJS question (helper text, type), and the inputs the Survey data tab's
 * segment control takes (segmentable questions, canonical question map,
 * tested-asset labels). Generic over any survey.
 */
export interface QuickRunReportData {
  surveyQuestionFor: (questionId: string) => any | null
  segmentableQuestions: Array<any>
  canonicalQuestionMap: Record<string, any> | null
  assetContextItems: Array<AssetReportContextItem> | null
}

export interface UseQuickRunReportDataInput {
  threadToken: string
  draftToken: string
  runToken: string
  /** Reads only while the document is open: the inline preview never asks. */
  isEnabled: boolean
}

const NO_RESPONSES: Array<any> = []

/**
 * The run's frozen survey and rows, read once per open document through the
 * run's own data route. A failure leaves the document as saved (null): the
 * extras it feeds are additive.
 */
export function useQuickRunReportData(input: UseQuickRunReportDataInput): QuickRunReportData | null {
  const { threadToken, draftToken, runToken, isEnabled } = input

  const [data, setData] = useState<QuickStudyExploreData | null>(null)

  useEffect(() => {
    if (!isEnabled || data?.runToken === runToken) {
      return
    }
    let isCurrent = true
    quickStudiesApi.runData(threadToken, draftToken, runToken).then(
      (loaded) => {
        if (isCurrent && loaded?.runToken === runToken) {
          setData(loaded)
        }
      },
      () => undefined,
    )
    return () => {
      isCurrent = false
    }
  }, [isEnabled, threadToken, draftToken, runToken, data?.runToken])

  const survey = data?.survey ?? null
  const { segmentableQuestions } = useReportQuestionCards({
    survey,
    responses: data?.responses ?? NO_RESPONSES,
    segmentationMappings: (data?.segmentationMappings as Array<SegmentationMapping> | null) ?? null,
  })

  return useMemo(() => {
    if (!survey) {
      return null
    }
    const canonicalQuestionMap: Record<string, any> | null = (survey as any)._canonicalConfig?.questionMap ?? null
    const questions: Array<any> = getAllQuestions(survey)
    return {
      // Findings questions carry the survey's question name; a canonical id also resolves.
      surveyQuestionFor: (questionId: string) =>
        questions.find((question) => question?.name === questionId) ??
        questions.find((question) => canonicalQuestionMap?.[question?.name]?.questionId === questionId) ??
        null,
      segmentableQuestions,
      canonicalQuestionMap,
      assetContextItems: (survey as any)._canonicalConfig?.assetContext?.items ?? null,
    }
  }, [survey, segmentableQuestions])
}
